/**
 * The one place a starter's still comes from: the whole-canvas og poster the
 * publish pipeline writes beside the snapshot. Built-in templates ship it
 * cropped to the card's box and pre-resampled at two widths, in light and in
 * dark (see assets/README.md), so the browser draws it near 1:1 on any
 * screen and the card sits in either theme. The first run's examples ship
 * theirs by story, under each environment's id. Anything else reads the
 * public files host, light only.
 */

import {
  FIRST_RUN_DEV_EXAMPLES,
  FIRST_RUN_PROD_EXAMPLES,
  FIRST_RUN_STACK_EXAMPLES,
  type FirstRunExampleStory,
} from "@/onboarding";
import { buildTemplateOgImageUrl } from "@/template-prompt/publicTemplateUrls";
import marathon360 from "./assets/483fe563-5d38-4220-963d-200bc5c29aaf-360.png";
import marathon720 from "./assets/483fe563-5d38-4220-963d-200bc5c29aaf-720.png";
import marathonDark360 from "./assets/483fe563-5d38-4220-963d-200bc5c29aaf-dark-360.png";
import marathonDark720 from "./assets/483fe563-5d38-4220-963d-200bc5c29aaf-dark-720.png";
import rack360 from "./assets/4882547c-8868-47ab-9279-dee9f1405006-360.png";
import rack720 from "./assets/4882547c-8868-47ab-9279-dee9f1405006-720.png";
import rackDark360 from "./assets/4882547c-8868-47ab-9279-dee9f1405006-dark-360.png";
import rackDark720 from "./assets/4882547c-8868-47ab-9279-dee9f1405006-dark-720.png";
import news360 from "./assets/4c60fa8a-2c9f-4214-b91a-251054208c24-360.png";
import news720 from "./assets/4c60fa8a-2c9f-4214-b91a-251054208c24-720.png";
import newsDark360 from "./assets/4c60fa8a-2c9f-4214-b91a-251054208c24-dark-360.png";
import newsDark720 from "./assets/4c60fa8a-2c9f-4214-b91a-251054208c24-dark-720.png";
import f1360 from "./assets/9f79f319-97c2-4704-9c88-62f44a27756a-360.png";
import f1720 from "./assets/9f79f319-97c2-4704-9c88-62f44a27756a-720.png";
import f1Dark360 from "./assets/9f79f319-97c2-4704-9c88-62f44a27756a-dark-360.png";
import f1Dark720 from "./assets/9f79f319-97c2-4704-9c88-62f44a27756a-dark-720.png";
import moon360 from "./assets/a96a6de6-77eb-4459-97aa-a1753796b6ce-360.png";
import moon720 from "./assets/a96a6de6-77eb-4459-97aa-a1753796b6ce-720.png";
import moonDark360 from "./assets/a96a6de6-77eb-4459-97aa-a1753796b6ce-dark-360.png";
import moonDark720 from "./assets/a96a6de6-77eb-4459-97aa-a1753796b6ce-dark-720.png";
import brioLight360 from "./assets/example-brio-360.png";
import brioLight720 from "./assets/example-brio-720.png";
import brioDark360 from "./assets/example-brio-dark-360.png";
import brioDark720 from "./assets/example-brio-dark-720.png";
import kumoLight360 from "./assets/example-kumo-360.png";
import kumoLight720 from "./assets/example-kumo-720.png";
import kumoDark360 from "./assets/example-kumo-dark-360.png";
import kumoDark720 from "./assets/example-kumo-dark-720.png";
import roveLight360 from "./assets/example-rove-360.png";
import roveLight720 from "./assets/example-rove-720.png";
import roveDark360 from "./assets/example-rove-dark-360.png";
import roveDark720 from "./assets/example-rove-dark-720.png";
import supperLight360 from "./assets/example-supper-360.png";
import supperLight720 from "./assets/example-supper-720.png";
import supperDark360 from "./assets/example-supper-dark-360.png";
import supperDark720 from "./assets/example-supper-dark-720.png";
import tesselLight360 from "./assets/example-tessel-360.png";
import tesselLight720 from "./assets/example-tessel-720.png";
import tesselDark360 from "./assets/example-tessel-dark-360.png";
import tesselDark720 from "./assets/example-tessel-dark-720.png";

export interface PosterSource {
  src: string;
  srcSet: string;
}

const WIDTHS = [360, 720] as const;

function source(urls: readonly [string, string]): PosterSource {
  return {
    src: urls[1],
    srcSet: urls.map((url, i) => `${url} ${WIDTHS[i]}w`).join(", "),
  };
}

export type PosterMode = "light" | "dark";

const EXAMPLE_POSTERS: Record<
  FirstRunExampleStory,
  Record<PosterMode, PosterSource>
> = {
  rove: {
    light: source([roveLight360, roveLight720]),
    dark: source([roveDark360, roveDark720]),
  },
  tessel: {
    light: source([tesselLight360, tesselLight720]),
    dark: source([tesselDark360, tesselDark720]),
  },
  supper: {
    light: source([supperLight360, supperLight720]),
    dark: source([supperDark360, supperDark720]),
  },
  brio: {
    light: source([brioLight360, brioLight720]),
    dark: source([brioDark360, brioDark720]),
  },
  kumo: {
    light: source([kumoLight360, kumoLight720]),
    dark: source([kumoDark360, kumoDark720]),
  },
};

const BUNDLED_POSTERS: Record<string, Record<PosterMode, PosterSource>> = {
  ...Object.fromEntries(
    [
      FIRST_RUN_PROD_EXAMPLES,
      FIRST_RUN_DEV_EXAMPLES,
      FIRST_RUN_STACK_EXAMPLES,
    ].flatMap((examples) =>
      (Object.keys(EXAMPLE_POSTERS) as FirstRunExampleStory[]).map((story) => [
        examples[story],
        EXAMPLE_POSTERS[story],
      ]),
    ),
  ),
  "4882547c-8868-47ab-9279-dee9f1405006": {
    light: source([rack360, rack720]),
    dark: source([rackDark360, rackDark720]),
  },
  "a96a6de6-77eb-4459-97aa-a1753796b6ce": {
    light: source([moon360, moon720]),
    dark: source([moonDark360, moonDark720]),
  },
  "9f79f319-97c2-4704-9c88-62f44a27756a": {
    light: source([f1360, f1720]),
    dark: source([f1Dark360, f1Dark720]),
  },
  "483fe563-5d38-4220-963d-200bc5c29aaf": {
    light: source([marathon360, marathon720]),
    dark: source([marathonDark360, marathonDark720]),
  },
  "4c60fa8a-2c9f-4214-b91a-251054208c24": {
    light: source([news360, news720]),
    dark: source([newsDark360, newsDark720]),
  },
};

/**
 * The card's width in CSS pixels: the column at one card, else the
 * production card track and what it stretches to (277px at 1280 and up).
 */
export const POSTER_SIZES = "(max-width: 555px) calc(100vw - 2rem), 277px";

/** The og poster's public, unsigned URL. */
export function posterUrl(templateId: string): string {
  return buildTemplateOgImageUrl(templateId);
}

/**
 * The bundled, resampled poster of a built-in template in the theme the
 * canvas cards draw their heroes in, if there is one; light when the theme
 * has no poster.
 */
export function bundledPoster(
  templateId: string,
  mode: string = "light",
): PosterSource | undefined {
  const posters = BUNDLED_POSTERS[templateId];
  if (!posters) return undefined;
  return mode === "dark" ? posters.dark : posters.light;
}
